import React, { useEffect, useRef, useState } from 'react';
import { Clock3, Eye, Heart, MapPin, Star } from 'lucide-react';
import { useTranslations } from '../translations';
import { Product } from '../services/api.types';
import { ApiError } from '../services/api.error';
import { addProductFavorite, removeProductFavorite } from '../services/product.api';
import { useNotification } from './NotificationProvider';
import { createPageUrl } from '../utils/appRoutes';
import { getPublishedAt } from '../utils/productDate';
import { formatProductPrice } from '../utils/unitType';
import ProductImagePlaceholder from './ProductImagePlaceholder';
import './ProductCard.css';

interface ProductCardProps {
  product: Product;
  viewType: 'list' | 'grid';
  onNavigateToProductDetail: (productId: string) => void;
  isLoggedIn?: boolean;
  onNavigateToLogin?: () => void;
  onFavoriteChange?: (productId: string, favorite: boolean) => void;
  showFavorite?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewType,
  onNavigateToProductDetail,
  isLoggedIn = false,
  onNavigateToLogin,
  onFavoriteChange,
  showFavorite = true,
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [isFavorite, setIsFavorite] = useState(Boolean(product.favorite));
  const [favoriteUpdating, setFavoriteUpdating] = useState(false);
  const favoritePropRef = useRef({
    productId: product.id,
    favorite: Boolean(product.favorite),
  });
  const ratingCount = product.ratingCount ?? 0;
  const hasReviews = ratingCount > 0;
  const productTypeLabel = product.productType === 'DEMAND'
    ? t.productCard.buyType
    : t.productCard.sellType;
  const reviewCountLabel = (
    ratingCount === 1 ? t.productCard.oneReview : t.productCard.multipleReviews
  ).replace('{count}', ratingCount.toString());
  const ratingLabel = hasReviews
    ? `${new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(product.rate ?? 0)} (${reviewCountLabel})`
    : t.productCard.noReviews;
  const viewCount = product.views ?? 0;
  const viewsLabel = t.productCard.viewsCompact.replace('{count}', viewCount.toString());

  useEffect(() => {
    const nextFavorite = Boolean(product.favorite);
    const previous = favoritePropRef.current;

    if (previous.productId === product.id && previous.favorite === nextFavorite) {
      return;
    }

    favoritePropRef.current = { productId: product.id, favorite: nextFavorite };
    setIsFavorite(nextFavorite);
  }, [product.id, product.favorite]);

  const formatLocation = () => {
    const locationName = product.locationDto.diacriticsName || product.locationDto.name;
    return `${locationName}, ${product.locationDto.county.name}`;
  };

  const formatDate = () => {
    const publishedAt = new Date(getPublishedAt(product));
    const updatedAt = product.updatedAt ? new Date(product.updatedAt) : null;
    const isUpdated = Boolean(
      updatedAt
      && !Number.isNaN(updatedAt.getTime())
      && updatedAt.getTime() - publishedAt.getTime() > 60_000,
    );
    const date = isUpdated && updatedAt ? updatedAt : publishedAt;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfProductDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysAgo = Math.round((startOfToday.getTime() - startOfProductDay.getTime()) / 86_400_000);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const dateLabel = daysAgo === 0
      ? 'azi'
      : daysAgo === 1
        ? 'ieri'
        : new Intl.DateTimeFormat('ro-RO', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(date);

    return (isUpdated ? t.productCard.updatedAt : t.productCard.publishedAt)
      .replace('{date}', dateLabel)
      .replace('{time}', `${hours}:${minutes}`);
  };

  const handleOpenProduct = () => {
    onNavigateToProductDetail(product.id);
  };

  const handleProductLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    handleOpenProduct();
  };

  const handleFavoriteClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isLoggedIn) {
      onNavigateToLogin?.();
      return;
    }
    if (favoriteUpdating) return;

    const nextFavorite = !isFavorite;
    setIsFavorite(nextFavorite);
    setFavoriteUpdating(true);

    try {
      if (nextFavorite) {
        await addProductFavorite(product.id);
      } else {
        await removeProductFavorite(product.id);
      }
      onFavoriteChange?.(product.id, nextFavorite);
    } catch (error) {
      setIsFavorite(!nextFavorite);
      if (error instanceof ApiError && error.hasErrorCode('product_self_favorite_not_allowed')) {
        showNotification(t.productCard.ownFavoriteNotAllowed, 'error');
      } else {
        showNotification(t.productCard.favoriteUpdateError, 'error');
      }
    } finally {
      setFavoriteUpdating(false);
    }
  };

  const openProductLabel = t.productCard.openProduct.replace('{title}', product.title);
  const location = formatLocation();
  const priceLabel = formatProductPrice(product.price);
  const productUrl = createPageUrl('productDetail', { id: product.id, title: product.title });

  return (
    <article
      className={`product-card product-card-${viewType}`}
    >
      <a
        className="product-card-link-overlay"
        href={productUrl}
        onClick={handleProductLinkClick}
        aria-label={openProductLabel}
      >
        <span className="product-card-link-text">{product.title}</span>
      </a>
      <div className="product-card-image-area">
        {product.image?.imageUrl ? (
          <img src={product.image.thumbnailUrl || product.image.imageUrl} alt={product.title} className="product-card-image" />
        ) : (
          <ProductImagePlaceholder label={t.productCard.noImage} />
        )}
        <span className={`product-card-type-badge ${product.productType === 'DEMAND' ? 'product-card-type-buy' : 'product-card-type-sale'}`}>
          {productTypeLabel}
        </span>
      </div>

      <div className="product-card-content">
        <h3 className="product-card-title" title={product.title}>{product.title}</h3>

        <div className="product-card-price-row">
          <span className="product-card-price" title={priceLabel}>{priceLabel}</span>
          {product.negotiable && (
            <span className="product-card-negotiable">{t.productDetail.negotiable}</span>
          )}
        </div>

        <div className="product-card-location">
          <MapPin size={15} aria-hidden="true" />
          <span title={location}>{location}</span>
        </div>

        <div className={`product-card-rating ${hasReviews ? 'has-reviews' : 'no-reviews'}`}>
          <Star size={15} fill={hasReviews ? 'currentColor' : 'none'} aria-hidden="true" />
          <span>{ratingLabel}</span>
        </div>

        <div className="product-card-footer">
          <span className="product-card-meta-item product-card-date">
            <Clock3 size={14} aria-hidden="true" />
            <span>{formatDate()}</span>
          </span>
          <span className="product-card-meta-item product-card-views" aria-label={viewsLabel}>
            <Eye size={14} aria-hidden="true" />
            <span>{viewsLabel}</span>
          </span>
        </div>
      </div>

      {showFavorite && (
        <button
          type="button"
          className={`product-card-favorite-button ${isFavorite ? 'active' : ''}`}
          onClick={handleFavoriteClick}
          disabled={favoriteUpdating}
          aria-pressed={isFavorite}
          aria-label={isFavorite ? t.productCard.removeFavorite : t.productCard.addFavorite}
          title={isFavorite ? t.productCard.removeFavorite : t.productCard.addFavorite}
        >
          <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
        </button>
      )}
    </article>
  );
};

export default ProductCard;
